import React from 'react';
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Person as PersonIcon,
  EmojiEvents as EmojiEventsIcon,
  MonetizationOn as ExperienceIcon,
  EmojiEvents as BadgeIcon,
} from '@mui/icons-material';
import Button from '../UIKit/Button';
import TextField from '../UIKit/TextField';
import { useTranslation } from 'react-i18next';

const BadgeSeriesRewardDialog = ({
  open,
  onClose,
  selectedSeries,
  selectedUser,
  setSelectedUser,
  availableUsers,
  loadingUsers,
  submitLoading,
  handleAwardSeriesReward,
}) => {
  const { t } = useTranslation(['badges', 'common']);
  
  // Определяем, есть ли опыт и специальный значок в серии
  const hasExperienceReward = selectedSeries?.bonusRewards?.experience > 0;
  const hasSpecialBadgeReward = !!selectedSeries?.bonusRewards?.specialBadge;

  // Определяем, какие награды будут выданы
  const experienceReward = hasExperienceReward
    ? selectedSeries?.bonusRewards?.experience
    : 0;

  // Проверка, есть ли вообще награды
  const hasAnyRewards = hasExperienceReward || hasSpecialBadgeReward;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <EmojiEventsIcon sx={{ mr: 1 }} />
          {t('badges:badgeSeriesRewardDialog.title')}{' '}
          {selectedSeries?.name ? `"${selectedSeries.name}"` : ''}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 0 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={availableUsers}
              loading={loadingUsers}
              getOptionLabel={(option) =>
                `${option.fullName} (${option.email})`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('badges:badgeSeriesRewardDialog.selectUser')}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1" component="span">
                    {option.fullName}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ ml: 1, color: 'text.secondary' }}
                  >
                    ({option.email})
                  </Typography>
                </li>
              )}
              value={selectedUser}
              onChange={(event, newValue) => {
                setSelectedUser(newValue);
              }}
            />
          </Grid>

          {hasAnyRewards && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('badges:badgeSeriesRewardDialog.rewards')}
              </Typography>
              <List dense disablePadding>
                {hasExperienceReward && (
                  <ListItem disableGutters sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ExperienceIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${experienceReward} XP`}
                      secondary={t('badges:badgeSeriesRewardDialog.experience')}
                    />
                  </ListItem>
                )}
                {hasSpecialBadgeReward && (
                  <ListItem disableGutters sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <BadgeIcon color="secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('badges:badgeSeriesRewardDialog.specialBadge')}
                      secondary={t('badges:badgeSeriesRewardDialog.specialBadgeDesc')}
                    />
                  </ListItem>
                )}
              </List>
            </Grid>
          )}

          {!hasAnyRewards && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('badges:badgeSeriesRewardDialog.noRewards')}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          onClick={() => {
            if (selectedUser && selectedSeries) {
              handleAwardSeriesReward(selectedUser._id, selectedSeries._id);
            }
          }}
          variant="contained"
          color="primary"
          startIcon={<EmojiEventsIcon />}
          disabled={submitLoading || !selectedUser || !selectedSeries}
        >
          {submitLoading ? (
            <CircularProgress size={24} />
          ) : (
            t('badges:badgeSeriesRewardDialog.giveReward')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BadgeSeriesRewardDialog;
